import { useAuth } from './useAuth';
import { useMemo } from 'react';
import { getToken } from '../auth';

/**
 * Decodifica un token JWT sin verificar la firma
 * Solo para extraer claims en el cliente
 */
const decodeToken = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Hook para verificar permisos del usuario autenticado
 * Los permisos vienen del token JWT
 */
export const usePermissions = () => {
  const { user } = useAuth();

  // Extraer permisos del token JWT
  const permissions = useMemo(() => {
    console.log('=== usePermissions: Extrayendo permisos ===');
    console.log('User object:', user);

    if (!user) {
      console.log('No hay usuario, retornando []');
      return [];
    }

    const token = getToken();
    console.log('Token obtenido:', token ? token.substring(0, 50) + '...' : 'null');

    if (!token) {
      console.log('No hay token, retornando []');
      return [];
    }

    const decoded = decodeToken(token);
    console.log('Token decodificado:', decoded);
    console.log('Propiedad permission:', decoded?.permission);

    if (!decoded) {
      console.log('Error decodificando token, retornando []');
      return [];
    }

    // Los permisos pueden venir como un array o como claims individuales
    if (decoded.permission) {
      // Si hay múltiples permisos, vienen como array
      if (Array.isArray(decoded.permission)) {
        console.log('Permisos (array):', decoded.permission);
        return decoded.permission;
      }
      // Si hay un solo permiso, viene como string
      console.log('Permiso único:', [decoded.permission]);
      return [decoded.permission];
    }

    console.log('No se encontró propiedad permission en el token');
    return [];
  }, [user]);

  /**
   * Verifica si el usuario tiene un permiso específico
   * @param permission Código del permiso a verificar
   * @returns true si el usuario tiene el permiso
   */
  const hasPermission = (permission: string): boolean => {
    return permissions.includes(permission);
  };

  /**
   * Verifica si el usuario tiene al menos uno de los permisos especificados
   * @param requiredPermissions Array de códigos de permisos
   * @returns true si el usuario tiene al menos uno de los permisos
   */
  const hasAnyPermission = (requiredPermissions: string[]): boolean => {
    return requiredPermissions.some(permission => permissions.includes(permission));
  };

  /**
   * Verifica si el usuario tiene todos los permisos especificados
   * @param requiredPermissions Array de códigos de permisos
   * @returns true si el usuario tiene todos los permisos
   */
  const hasAllPermissions = (requiredPermissions: string[]): boolean => {
    return requiredPermissions.every(permission => permissions.includes(permission));
  };

  /**
   * Verifica si el usuario tiene un rol específico
   * @param role Descripción del rol
   * @returns true si el usuario tiene el rol
   */
  const hasRole = (role: string): boolean => {
    if (!user?.role) return false;
    // El rol puede venir como string o array
    if (Array.isArray(user.role)) {
      return user.role.includes(role);
    }
    return user.role === role;
  };

  /**
   * Verifica si el usuario es SuperAdmin
   * @returns true si el usuario tiene el rol SuperAdmin
   */
  const isSuperAdmin = (): boolean => {
    return hasRole('Super Administrador') || hasRole('SuperAdmin') || hasRole('SUPERADMIN');
  };

  return {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    isSuperAdmin
  };
};
